/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { classNames } from '#utils/UNSAFE_classNames';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useEffect, useMemo, useRef } from 'preact/hooks';
import { useUser } from '#hooks/UNSAFE_useUser';
import {
  usePointerEvents,
  useKeyboardEvents,
  useMeterDatatip
} from '#hooks/PRIVATE_useVisMeterEvents';

import {
  getMeterAriaProps,
  validateRange,
  getTrackAndIndicatorColor
} from '#utils/PRIVATE_meterUtils';

import { ReferenceLine, Threshold } from '#utils/UNSAFE_meterUtils';
import { BarTrack, BarValue } from '#PRIVATE_Meter';
import {
  getDimensions,
  getFitMarginClass,
  getRefLinesInfo,
  getValue,
  getFitTrackWidth,
  getFitTrackHeight,
  getComponentStyle,
  getSizedMargin
} from './meterUtils';
import { ReferenceLineBar } from './ReferenceLines';
import { ThresholdBar } from './ThresholdBar';
import { MeterBarProps } from './meterBar.types';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { MeterBarStyles, MeterBarVariantOptions } from './themes/MeterBarStyles.css';
import { MeterBarRedwoodTheme } from './themes/redwood/MeterBarTheme';
import { useTestId } from '#hooks/UNSAFE_useTestId';
import { meterBarVars } from './themes/MeterBarContract.css';
import { xUnits } from '#UNSAFE_Theme';
import {
  getDefaultReferenceLineLabelFontSize,
  getMaxRefLabelDims,
  hasLabelledReferenceLine
} from '#utils/PRIVATE_meterUtils/meterUtils';
import { useTextDimensions } from '#hooks/PRIVATE_useTextDimensions';
import { useCssVars } from '#hooks/PRIVATE_useCssVars';
import { typographyVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';

type MeterBarWithDimensionsProps = Omit<MeterBarProps, 'width' | 'height'> & {
  width?: number;
  height?: number;
};

export function MeterBarWithDimensions({
  max = 100,
  min = 0,
  value = 0,
  step = 1,
  length = '100%',
  width,
  height,
  size,
  orientation = 'horizontal',
  isTrackRendered = true,
  thresholdDisplay = 'all',
  indicatorSize = 1,
  testId,
  baseline,
  isReadonly,
  ...props
}: MeterBarWithDimensionsProps) {
  const testIdProps = useTestId(testId);

  validateRange(min, max, value, step, false, isReadonly, baseline);
  const currentInputRef = useRef<number>();

  const isHoriz = orientation === 'horizontal';
  const barTrackRef = useRef<HTMLDivElement>(null);
  const dimensionsRef = useRef(getDimensions(null));
  const { direction, forcedColors } = useUser();
  const isRtl = direction === 'rtl';

  useEffect(() => {
    if (props.onCommit || props.onInput) {
      dimensionsRef.current = getDimensions(barTrackRef.current);
    }
  }, [size, length, orientation, props.onCommit, props.onInput]);

  const { textMeasureContent, getTextDimensions } = useTextDimensions();

  const getValueFromEvent = (event: PointerEvent): number | undefined => {
    // only evaluate value from bar track
    if (event.target == barTrackRef.current) {
      return getValue(event, dimensionsRef, min, max, step, orientation, isRtl);
    }
    return;
  };

  const isPointerActiveRef = useRef(false);

  const pointerEventsProps = usePointerEvents(
    value,
    getValueFromEvent,
    barTrackRef,
    currentInputRef,
    isPointerActiveRef,
    props.onCommit,
    props.onInput
  );

  const keyboardEventsProps = useKeyboardEvents(
    value,
    min,
    max,
    step,
    currentInputRef,
    props.onCommit,
    props.onInput
  );

  const { datatipContent, datatipProps } = useMeterDatatip(
    value,
    isPointerActiveRef,
    props.datatip,
    props['aria-describedby']
  );

  const ariaLabelProps = getMeterAriaProps(
    value,
    min,
    max,
    props['aria-label'],
    props['aria-labelledby'],
    props.thresholds,
    isReadonly
  );
  const mergedProps = mergeProps(
    pointerEventsProps,
    keyboardEventsProps,
    datatipProps,
    ariaLabelProps
  );
  const resolvedBaseline = baseline !== undefined ? Math.max(min, Math.min(baseline, max)) : min;
  const baselineOffsetPercent = ((resolvedBaseline - min) * 100) / (max - min);
  const valueBarOffsetPercent = ((value - min) * 100) / (max - min);

  const valueBarStartOffset = Math.min(baselineOffsetPercent, valueBarOffsetPercent);
  const valueBarLength = `${Math.abs(baselineOffsetPercent - valueBarOffsetPercent)}%`;

  const valueBarSize = `${Math.max(0, Math.min(1, indicatorSize)) * 100}%`;
  const { trackColor, indicatorColor } = getTrackAndIndicatorColor(
    value,
    thresholdDisplay,
    props.trackColor,
    props.indicatorColor,
    props.thresholds
  );

  const refLineInfo = getRefLinesInfo(props.referenceLines);
  const isInteractive = props.onCommit || props.onInput;
  const { isTabbable } = useTabbableMode();

  const {
    classes,
    styles: {
      interactiveStyle,

      barValueBaseStyle,
      barValueVerticalStyle,
      barValueHorizontalStyle,
      barValueHighContrastStyle,

      barTrackBaseStyle,

      barTrackHorizontalSizeStyle,
      barTrackVerticalSizeStyle,
      barTrackBorderStyle,

      barTrackMarginTopFitStyle,
      barTrackMarginStartFitStyle,
      barTrackMarginBottomFitStyle,
      barTrackMarginEndFitStyle,

      trackSectionHorizontalFirstStyle,
      trackSectionHorizontalLastStyle,

      trackSectionVerticalFirstStyle,
      trackSectionVerticalLastStyle,

      trackSectionRoundedCornersStyle
    }
  } = useComponentTheme<MeterBarVariantOptions, MeterBarStyles>(MeterBarRedwoodTheme, {
    size,
    orientation
  });

  const isFit = size === 'fit';
  const hasReferenceLabel = hasLabelledReferenceLine(props.referenceLines);
  const fontSize = getDefaultReferenceLineLabelFontSize('sm');

  const meterBarCssVars = useMemo(
    () => [
      {
        tokenVar: fontSize,
        key: 'labelFontSize',
        cssProp: 'font-size'
      },
      {
        tokenVar: typographyVars.body.md.lineHeight,
        key: 'lineHeight',
        cssProp: 'line-height'
      }
    ],
    [fontSize]
  );

  const { resolvedVars, cssContent } = useCssVars(meterBarCssVars);

  const { dims, maxRefLabelDims } = getMaxRefLabelDims(
    resolvedVars['fontSize'],
    resolvedVars['lineHeight'],
    props.referenceLines,
    getTextDimensions
  );

  const fitTrackWidth = getFitTrackWidth(refLineInfo, maxRefLabelDims.width, width, isHoriz);
  const fitTrackHeight = getFitTrackHeight(refLineInfo, maxRefLabelDims.height, height, isHoriz);
  const fitTrackSize = isHoriz ? fitTrackHeight : fitTrackWidth;

  return (
    <>
      <div
        tabIndex={!isTabbable ? -1 : 0}
        {...testIdProps}
        {...mergedProps}
        style={getComponentStyle(
          isFit,
          isHoriz,
          length,
          maxRefLabelDims,
          fitTrackHeight,
          fitTrackWidth
        )}
        class={classNames([
          classes,
          ...(isFit
            ? getFitMarginClass(
                refLineInfo,
                isHoriz,
                barTrackMarginTopFitStyle,
                barTrackMarginStartFitStyle,
                barTrackMarginBottomFitStyle,
                barTrackMarginEndFitStyle
              )
            : [])
        ])}>
        <BarTrack
          ref={barTrackRef}
          orientation={orientation}
          color={
            !isTrackRendered || (thresholdDisplay === 'all' && props.thresholds)
              ? 'transparent'
              : trackColor
          }
          class={classNames([
            barTrackBaseStyle,
            isInteractive ? interactiveStyle : '',
            isHoriz ? barTrackHorizontalSizeStyle : barTrackVerticalSizeStyle,
            !isTrackRendered || (thresholdDisplay === 'all' && props.thresholds)
              ? undefined
              : barTrackBorderStyle
          ])}
          style={{
            margin: !isFit
              ? getSizedMargin(
                  refLineInfo,
                  isHoriz,
                  !!props.referenceLines?.length,
                  hasReferenceLabel,
                  maxRefLabelDims
                )
              : undefined
          }}>
          {thresholdDisplay === 'all' &&
            props.thresholds
              ?.filter((threshold: Threshold) => threshold.max > min && threshold.max <= max)
              .sort((a, b) => a.max - b.max)
              .map((threshold: Threshold, index, thresholds) => {
                const diff =
                  index === 0 ? threshold.max - min : threshold.max - thresholds[index - 1].max;
                const trackSectionLastStyle = isHoriz
                  ? trackSectionHorizontalLastStyle
                  : trackSectionVerticalLastStyle;
                let trackSectionStyleClass =
                  index === 0
                    ? isHoriz
                      ? trackSectionHorizontalFirstStyle
                      : trackSectionVerticalFirstStyle
                    : '';
                if (threshold.max === max)
                  trackSectionStyleClass =
                    index === 0
                      ? trackSectionRoundedCornersStyle // Single threshold from min to max
                      : trackSectionLastStyle;
                return (
                  <>
                    <ThresholdBar
                      class={trackSectionStyleClass}
                      orientation={orientation}
                      length={`${(diff * 100) / (max - min)}%`}
                      color={threshold.color}
                    />
                    {isTrackRendered && index === thresholds.length - 1 && threshold.max < max ? (
                      <ThresholdBar
                        class={trackSectionLastStyle}
                        color={trackColor}
                        orientation={orientation}
                        length={`${((max - thresholds[index].max) * 100) / (max - min)}%`}
                      />
                    ) : (
                      ''
                    )}
                  </>
                );
              })}
          <BarValue
            left={isHoriz && !isRtl ? `${valueBarStartOffset}%` : undefined}
            bottom={!isHoriz ? `${valueBarStartOffset}%` : undefined}
            right={isHoriz && isRtl ? `${valueBarStartOffset}%` : undefined}
            width={isHoriz ? valueBarLength : '100%'}
            height={isHoriz ? '100%' : valueBarLength}
            length={valueBarLength}
            size={valueBarSize}
            orientation={orientation}
            class={classNames([
              barValueBaseStyle,
              isHoriz ? barValueHorizontalStyle : barValueVerticalStyle,
              forcedColors === 'active' ? barValueHighContrastStyle : ''
            ])}
            color={indicatorColor}
          />
          {props.referenceLines?.map((refLine: ReferenceLine, index: number) => (
            <ReferenceLineBar
              label={refLine.label}
              labelStyle={{
                fontSize: resolvedVars['labelFontSize'],
                ...refLine.labelStyle
              }}
              color={refLine.color}
              maxLabelWidth={dims[index]?.width}
              maxLabelHeight={dims[index]?.height}
              orientation={orientation}
              percent={((refLine.value - min) * 100) / (max - min)}
              position={refLine.position}
              trackSize={isFit ? `${fitTrackSize}px` : meterBarVars.size}
              length={isFit ? `calc(${xUnits(4)} + ${fitTrackSize}px)` : undefined}
            />
          ))}
        </BarTrack>
      </div>
      {cssContent}
      {datatipContent}
      {textMeasureContent}
    </>
  );
}
